import { isString } from 'lodash';
import { ParsedQuery } from 'query-string';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { ProjectId } from 'api/models';
import { FilterableField } from 'components/organisms/tasks/fields';
import { Transformer as HashTransformer, useHash } from 'hooks/use-hash';
import { useLocalStorage } from 'hooks/use-local-storage';
import {
  defaultKanbanBoardColumnGroup,
  defaultKanbanBoardRowGroup,
} from '../tasks/constants';

export type GroupBy = {
  rowGroup: RowGroup;
  columnGroup: ColumnGroup;
};
type Empty = ['empty'];
type RowGroup = null | string;
type ColumnGroup = string;
type Valid = ['valid', { rowGroup: RowGroup; columnGroup: ColumnGroup | null }];
type HashResult = Empty | Valid;
type GroupByParseQuery = ParsedQuery & Partial<GroupBy>;
const transformer = (
  fieldLookup: Record<string, FilterableField>,
): HashTransformer<HashResult> => ({
  from: ({ rowGroup, columnGroup }: GroupByParseQuery = {}) => {
    if (!rowGroup && !columnGroup) {
      return ['empty'];
    }

    const isValid = (value) => {
      return value != null && isString(value) && fieldLookup[value];
    };

    const row = isValid(rowGroup) ? (rowGroup as string) : null;
    const column = isValid(columnGroup) ? (columnGroup as string) : null;

    return ['valid', { rowGroup: row, columnGroup: column }];
  },
  to: ([status, result]) => {
    switch (status) {
      case 'empty':
        return {
          rowGroup: null,
          columnGroup: null,
        };
      case 'valid':
      default:
        return { ...result };
    }
  },
});

const DEFAULT_GROUP_BY = {
  rowGroup: null,
  columnGroup: 'status',
};

export const useKanbanGroupBy = (
  [, projectId]: ProjectId,
  fieldLookup: Record<string, FilterableField>,
): [GroupBy, Dispatch<SetStateAction<GroupBy>>] => {
  const [, setHashQuery] = useHash(transformer(fieldLookup));
  const [localStorageState, setGroupBy] = useLocalStorage<GroupBy>(
    `task_board.group_by.project_id-${projectId}`,
    DEFAULT_GROUP_BY,
  );
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) {
      return;
    }

    setHashQuery(['valid', localStorageState], true);
    setInitialized(true);
  }, [initialized, setInitialized, localStorageState, setHashQuery]);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    setHashQuery(['valid', localStorageState]);
  }, [initialized, localStorageState, setHashQuery]);

  return [localStorageState, setGroupBy];
};

export const useAllTasksKanbanGroupBy = (
  fieldLookup: Record<string, FilterableField>,
  fieldsLoading: boolean,
): [GroupBy, (value: GroupBy, replace?: boolean) => void] => {
  const [[, fromHash], setHashQuery] = useHash(transformer(fieldLookup));
  let rowGroup = fromHash?.rowGroup || null;
  let columnGroup = fromHash?.columnGroup;

  if (!columnGroup) {
    columnGroup = defaultKanbanBoardColumnGroup;
    if (!rowGroup) {
      rowGroup = defaultKanbanBoardRowGroup;
    }
  }
  const initial: GroupBy = useMemo(() => {
    return {
      rowGroup,
      columnGroup: columnGroup!,
    };
  }, [rowGroup, columnGroup]);
  const [state, setState] = useState<GroupBy>(initial);
  const [initialized, setInitialized] = useState(false);

  const setGroupBy = (value: GroupBy, replace = false) => {
    setState(value);
    setHashQuery(['valid', value], replace);
  };

  useEffect(() => {
    if (initialized || fieldsLoading) {
      return;
    }

    setState(initial);
    setHashQuery(['valid', initial], true);
    setInitialized(true);
  }, [
    initialized,
    setInitialized,
    state,
    setHashQuery,
    fieldsLoading,
    initial,
  ]);

  return [initial, setGroupBy];
};
